import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setQuery, setPage } from "../../store/slices/holidayBalancesSlice.ts";
import { RootState } from "../../store";
import { readBranches } from "../../services/branch.service.ts";
import { notification } from "../../utils/Notifications.tsx";

const parseParamFromQuery = (query: string): string => {
  if (!query) return "";
  if (query.includes("param=")) {
    return decodeURIComponent(query.split("param=")[1]?.split("&")[0] || "");
  }
  return query;
};

const parseQueryValue = (query: string, key: string, fallback = "all"): string => {
  if (!query.includes(`${key}=`)) return fallback;
  return decodeURIComponent(query.split(`${key}=`)[1]?.split("&")[0] || fallback);
};

const FilterHolidayBalances: React.FC = () => {
  const dispatch = useDispatch();
  const { query } = useSelector((state: RootState) => state.holidayBalances);
  const [timer, setTimer] = React.useState<any>(null);
  const [showPills, setShowPills] = React.useState(false);
  const [branches, setBranches] = useState<any[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  
  const initialValues = {
    param: "",
    branchId: "all",
    vacationType: "all",
  };
  
  const [filters, setFilters] = useState({
    param: parseParamFromQuery(query),
    branchId: parseQueryValue(query, "branchId"),
    vacationType: parseQueryValue(query, "vacationType"),
  });
  const [searchParam, setSearchParam] = React.useState(parseParamFromQuery(query));

  useEffect(() => {
    const parsedParam = parseParamFromQuery(query);
    setSearchParam(parsedParam);
    setFilters({
      param: parsedParam,
      branchId: parseQueryValue(query, "branchId"),
      vacationType: parseQueryValue(query, "vacationType"),
    });
    setShowPills(Boolean(query));
  }, [query]);

  const updateFilters = (params?: {}) => {
    const filtros = { ...filters, ...params };
    const queryString = Object.keys(filtros)
      .filter(
        (key) =>
          filtros[key] !== -1 && filtros[key] !== "" && filtros[key] !== "all"
      )
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(filtros[key])}`
      )
      .join("&");
    dispatch(setQuery(queryString));
    dispatch(setPage(0));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchParam(newValue);
    setFilters((prevFilters) => ({ ...prevFilters, param: newValue }));
    
    if (newValue === "") {
      setShowPills(false);
    } else {
      setShowPills(true);
    }
    
    if (timer) {
      clearTimeout(timer);
    }
    
    setTimer(
      setTimeout(() => {
        const updatedFilters = { ...filters, param: newValue };
        const queryString = Object.keys(updatedFilters)
          .filter(
            (key) => updatedFilters[key] !== -1 && updatedFilters[key] !== "" && updatedFilters[key] !== "all"
          )
          .map(
            (key) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(
                updatedFilters[key]
              )}`
          )
          .join("&");
        dispatch(setQuery(queryString));
        dispatch(setPage(0));
        setTimer(null);
      }, 500)
    );
  };

  const clearSearch = () => {
    setSearchParam("");
    setFilters(initialValues);
    dispatch(setQuery(""));
    dispatch(setPage(0));
    setShowPills(false);
  };

  // Función para cargar sucursales
  const fetchBranches = async () => {
    try {
      setLoadingBranches(true);
      const resp = await readBranches();
      setBranches(resp || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      notification("Error", "Error al cargar sucursales", "danger");
    } finally {
      setLoadingBranches(false);
    }
  };

  // Cargar sucursales al montar el componente
  useEffect(() => {
    fetchBranches();
  }, []);

  const selectedFilters = (filters: any) => {
    let selected: any = [];
    for (const key in filters) {
      if (filters[key] !== "all" && filters[key] !== "") {
        let value = filters[key];
        let valueName = "";
        let keyName = "";
        switch (key) {
          case "param":
            keyName = "Búsqueda";
            valueName = value;
            break;
          case "branchId":
            keyName = "Sucursal";
            const branch = branches.find(b => b.id === parseInt(value));
            valueName = branch ? branch.name : value;
            break;
          case "vacationType":
            keyName = "Tipo de vacaciones";
            switch (value) {
              case "pendingDays":
                valueName = "Con días pendientes";
                break;
              case "usedVacations":
                valueName = "Que han usado vacaciones";
                break;
              default:
                valueName = value;
            }
            break;
          default:
            keyName = key;
            valueName = value;
            break;
        }
        selected.push({ key, value, valueName, keyName });
      }
    }

    if (selected.length === 0) setShowPills(false);
    return selected;
  };

  const removeFilter = (key: string) => {
    setFilters({ ...filters, [key]: initialValues[key] });

    const updatedFilters = { ...filters, [key]: initialValues[key] };
    const queryString = Object.keys(updatedFilters)
      .filter(
        (key) =>
          updatedFilters[key] !== -1 &&
          updatedFilters[key] !== "" &&
          updatedFilters[key] !== "all"
      )
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(
            updatedFilters[key]
          )}`
      )
      .join("&");
    dispatch(setQuery(queryString));
    dispatch(setPage(0));
  };

  return (
    <div className="w-100">
      <div className="input-group mb-3">
        <button
          className="btn btn-white dropdown-toggle border"
          type="button"
          data-bs-toggle="dropdown"
          style={{ borderRadius: "10px 0 0 10px" }}
        >
          <span className="d-none d-md-inline">Filtrar</span>
          <span className="d-inline d-md-none">
            <i className="fa fa-filter"></i>
          </span>
          <b className="caret"></b>
        </button>
        <div className="dropdown-menu p-4" onClick={(e) => e.stopPropagation()}>
          <label>Sucursal:</label>
          <select
            className="form-select mt-1"
            id="branchId"
            name="branchId"
            onChange={(e) => setFilters({ ...filters, branchId: e.target.value })}
            value={filters.branchId}
            disabled={loadingBranches}
          >
            <option value={"all"}>Todas las sucursales</option>
            {branches?.map((branch: any) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>

          <label className="mt-3">Tipo de vacaciones:</label>
          <select
            className="form-select mt-1"
            id="vacationType"
            name="vacationType"
            onChange={(e) => setFilters({ ...filters, vacationType: e.target.value })}
            value={filters.vacationType}
          >
            <option value={"all"}>Todos</option>
            <option value={"pendingDays"}>Con días pendientes</option>
            <option value={"usedVacations"}>Que han usado vacaciones</option>
          </select>

          <div className="row mt-3 mt-1">
            <div className="col">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFilters({
                    param: "",
                    branchId: "all",
                    vacationType: "all",
                  });
                  dispatch(setQuery(""));
                  setShowPills(false);
                }}
                className="btn btn-amarillo d-block w-100 btn-lg fs-14px"
              >
                Limpiar
              </button>
            </div>
            <div className="col">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  updateFilters();
                  setShowPills(true);
                }}
                className="btn btn-azul d-block w-100 btn-lg fs-14px"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>

        <div className="flex-fill position-relative">
          <div className="input-group">
            <div
              className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
              style={{ zIndex: 10 }}
            >
              {timer ? (
                <i className="fa fa-spinner fa-spin"></i>
              ) : (
                <i className="fa fa-search opacity-5"></i>
              )}
            </div>
            <input
              type="text"
              className="form-control px-35px bg-light border-start-0"
              style={{ borderRadius: "0 10px 10px 0" }}
              placeholder="Buscar por nombre del colaborador..."
              id="name"
              name="name"
              onChange={(e) => handleSearchChange(e)}
              value={searchParam}
            />
          </div>
        </div>
      </div>
      <div className="mb-3 w-100">
        {showPills &&
          selectedFilters(filters).map((filter: any) => (
            <div
              key={filter.key}
              className="badge bg-primary text-white fs-6 me-2 position-relative pe-4"
            >
              {filter.keyName}: {filter.valueName}
              <button
                type="button"
                className="btn-close btn-close-white position-absolute top-0 end-0 mt-1 me-1 ps-1s"
                aria-label="Close"
                onClick={() => removeFilter(filter.key)}
              ></button>
            </div>
          ))}
      </div>
    </div>
  );
};

export default FilterHolidayBalances;
